import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Star, Car, ScanFace } from "lucide-react";
import { IMAGES, getConfig } from "../mock/mock";
import PricingSection from "../components/PricingSection";
import {
  StatsSection, ToolsSection, HowToSection, UseCasesSection,
  TestimonialsSection, FAQSection, FinalCTA,
} from "../components/HomeSections";

const Hero = () => (
  <section className="bg-[#FFF7F0] pt-14 sm:pt-20 pb-16 sm:pb-20">
    <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
      {/* texto */}
      <div className="animate-rise">
        <p className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FDEBDC] text-[#C2570F] text-[12px] font-extrabold uppercase tracking-[0.12em] mb-6">
          <ShieldCheck size={13} /> Plataforma de privacidad Natur
        </p>
        <h1 className="font-display text-[36px] sm:text-[52px] font-extrabold text-[#241E18] leading-[1.08] tracking-tight">
          Difumina lo que quieras.{" "}
          <span className="text-[#F26F21]">Protege a todos.</span>
        </h1>
        <p className="text-[15.5px] text-[#8A7F73] mt-5 max-w-md leading-relaxed">
          Matrículas, caras, fondos y objetos: la IA de Natur Blur los detecta y los oculta automáticamente en tu vídeo, con seguimiento de movimiento real.
        </p>
        <div className="flex flex-wrap items-center gap-3.5 mt-8">
          <Link
            to="/editor"
            className="inline-flex items-center gap-2 h-13 px-8 py-4 rounded-full bg-[#F26F21] text-white text-[15.5px] font-extrabold hover:bg-[#D95B12] hover:shadow-xl hover:shadow-[#F26F21]/30 hover:-translate-y-0.5 transition-all"
          >
            Subir un vídeo <ArrowRight size={17} />
          </Link>
          <Link
            to="/tarifas"
            className="inline-flex items-center h-13 px-7 py-4 rounded-full border-2 border-[#241E18]/15 text-[#241E18] text-[15px] font-bold hover:border-[#F26F21] hover:text-[#F26F21] transition-all"
          >
            Ver tarifas
          </Link>
        </div>
        <div className="flex items-center gap-2 mt-8">
          <span className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={15} className="text-[#FFB81C] fill-[#FFB81C]" />
            ))}
          </span>
          <p className="text-[13px] font-bold text-[#57503F]">
            4,8/5 <span className="font-medium text-[#A69C90]">· más de 120.000 vídeos protegidos</span>
          </p>
        </div>
      </div>

      {/* visual */}
      <div className="relative animate-rise" style={{ animationDelay: "120ms" }}>
        <div className="relative rounded-[32px] overflow-hidden shadow-2xl shadow-[#F26F21]/15 border-4 border-white">
          <img src={IMAGES.street} alt="Ejemplo de difuminado en vídeo urbano" className="w-full h-[380px] sm:h-[440px] object-cover" />
          {/* parches simulados */}
          <div className="absolute z-10 rounded-lg backdrop-blur-lg bg-white/5 border-2 border-[#FFB81C]/70 patch-drift-a" style={{ top: "48%", left: "18%", width: "13%", height: "9%" }} />
          <div className="absolute z-10 rounded-lg backdrop-blur-lg bg-white/5 border-2 border-[#FFB81C]/70 patch-drift-b" style={{ top: "55%", left: "63%", width: "11%", height: "8%" }} />
        </div>
        {/* tarjetas flotantes */}
        <div className="absolute -left-3 sm:-left-6 top-8 rounded-2xl bg-white shadow-xl shadow-black/10 px-4 py-3 flex items-center gap-2.5 animate-float-a">
          <span className="w-9 h-9 rounded-xl bg-[#FFF3E9] text-[#F26F21] flex items-center justify-center"><Car size={17} /></span>
          <div>
            <p className="text-[12.5px] font-extrabold text-[#241E18]">Matrícula oculta</p>
            <p className="text-[11px] text-[#2FA45A] font-bold">Seguimiento activo ✓</p>
          </div>
        </div>
        <div className="absolute -right-3 sm:-right-6 bottom-10 rounded-2xl bg-white shadow-xl shadow-black/10 px-4 py-3 flex items-center gap-2.5 animate-float-b">
          <span className="w-9 h-9 rounded-xl bg-[#FFF3E9] text-[#F26F21] flex items-center justify-center"><ScanFace size={17} /></span>
          <div>
            <p className="text-[12.5px] font-extrabold text-[#241E18]">2 caras detectadas</p>
            <p className="text-[11px] text-[#8A7F73] font-semibold">Difuminado automático</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const [config, setConfigState] = useState(getConfig());

  useEffect(() => {
    const upd = () => setConfigState(getConfig());
    window.addEventListener("naturblur-config", upd);
    return () => window.removeEventListener("naturblur-config", upd);
  }, []);

  const s = config.sections;

  return (
    <main className="bg-white">
      {s.hero && <Hero />}
      {s.stats && <StatsSection />}
      {s.herramientas && <ToolsSection />}
      {s.comoFunciona && <HowToSection />}
      {s.casos && <UseCasesSection />}
      {s.tarifas && <PricingSection />}
      {s.testimonios && <TestimonialsSection />}
      {s.faq && <FAQSection />}
      {s.ctaFinal && <FinalCTA />}
    </main>
  );
};

export default HomePage;
